/**
 * "Add a PDApp icon" — the authoring surface of #788.
 *
 * TWO ways to get one, because the ICON is the subject and opening an app is an
 * option on top of it (Chu's review of the first cut, which offered only "link to
 * a PDApp" and so made the link the subject):
 *
 *   • DESIGN one — the PDApp icon maker, in produce mode: it hands the spec back
 *     instead of saving it onto an app. Nothing to link to, so no link.
 *   • COPY an existing PDApp's — its PORTABLE metadata through the one export
 *     (`GET /api/miniapp/:id/metadata`, gated on the author's own read), with an
 *     opt-in "also open it when clicked". OFF by default.
 *
 * The list of apps to copy from is `PdappAppPicker` — shared with the chat card,
 * because what makes it interesting is not the rows but what happens when one
 * turns out to be unreachable.
 *
 * Either way the result is a COPY of the icon at insert time. It does not follow
 * the target app's later changes — that is what a copy is; refreshing one means
 * selecting it in design mode and pressing the icon button again, which replaces
 * it in place. Clicking a LINKED icon is gated on the VIEWER, so a visitor
 * without access to the target gets a refusal and the author's app is told
 * nothing.
 */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ArrowLeft, Blocks, ChevronRight, Info, Palette, X } from 'lucide-react'
import { PlacementPicker, useTheme, getThemeClass, useToast } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'
// Deep subpath, not the barrel: this dialog is now opened from the CHAT too,
// and `@papan/files/ui` is the whole drive stack.
import { MiniappIconEditorDialog } from '@papan/files/ui/icon-editor'
import { MINIAPP_ICON_METADATA_KEY, type MiniappIconSpec, type Placement } from '@papan/types'
import { PdappAppPicker } from './PdappAppPicker.js'
import { artDidNotTravel, metadataFromDesignedIcon } from './pdapp-icon-metadata.js'
import { buildPdappIconMarkup, PDAPP_ICON_DEFAULT_PX, type PortablePdappMetadata } from '@papan/types/miniapp-icon-svg'

/** What was inserted — the caller words its own confirmation from this, and the
 *  agent-driven path reports it back to the model. */
export interface InsertedIconInfo {
  kind: 'designed' | 'copied'
  /** The app the icon came from; absent for a designed one. */
  name?: string
  /** Whether the block also opens that app when clicked. */
  linked: boolean
  /** The spec references uploaded art that did NOT travel with it — the export
   *  drops an asset over its byte ceiling, and the copy then draws the tile
   *  without the picture. Said out loud rather than left to be noticed. */
  artMissing?: boolean
}

/** The icon the author chose, in both the forms a caller might want it: the
 *  portable METADATA (what it is) and the finished MARKUP (what to write). The
 *  editor writes the markup; the chat card reports the metadata to the agent and
 *  lets it place the block. Built once, here, so the two cannot differ. */
export interface ChosenIcon {
  meta: PortablePdappMetadata
  markup: string
  info: InsertedIconInfo
}

/** What the caller made of it. The dialog needs the answer: a write can be
 *  refused (the document went read-only while the dialog was open), and closing
 *  on a refusal throws away a design the author just made. */
export interface IconInsertResult { ok: boolean; error?: string }

interface AddPdappIconDialogProps {
  /** Receives the chosen icon. The caller decides what happens to it — written
   *  into a document, or reported to whoever asked — and answers whether that
   *  worked; a refusal keeps the dialog open with the author's work intact. */
  onChoose: (chosen: ChosenIcon) => Promise<IconInsertResult> | IconInsertResult
  onClose: () => void
  /** The app being edited — left out of the list, because an app linking to
   *  itself is never what the author meant. */
  excludeFolderId?: string | null
  /** Tenant whose Drive the icon maker's image picker browses. Without it that
   *  source is hidden and only computer upload is offered (the editor's own
   *  graceful degradation). */
  tenantId?: string | null
  /** One line from the agent when the AGENT asked for the icon, so the author
   *  knows what they are being asked for rather than seeing a dialog open by
   *  itself. Plain text, rendered as text. */
  note?: string | null
  /** The file the icon will be written into. Several editors can be mounted at
   *  once and the agent can open this dialog over any of them, so naming the
   *  destination is the author's only chance to notice a wrong one. */
  targetFile?: string | null
  /** EDITING a designed icon: open the maker holding what it already looks like,
   *  rather than the chooser. A copied icon has an app behind it, so editing one
   *  means re-picking — which is the chooser's job, and it opens there. */
  initialSpec?: MiniappIconSpec | null
  /** EDITING a copied icon that opened its app: start with the option already
   *  ticked, so keeping it is not something the author has to remember. */
  initialLink?: boolean
  /** REPLACING the icon that is already there, rather than adding one. Only the
   *  wording changes — the caller owns what actually happens to the answer. */
  replacing?: boolean
  /** Passed straight to the maker's placement control — see its prop. The
   *  editor owns the semantics; this dialog only carries it across. */
  placement?: {
    label: string
    options: ReadonlyArray<{ value: Placement; label: string }>
    value: Placement
    onChange: (value: Placement) => void
  }
  /** Open straight at the app list, skipping the two-button chooser — for a
   *  caller that has ALREADY asked design-or-copy itself (landing on a second
   *  chooser would be the same question twice). The chat card opens at the
   *  chooser: its Customize is that question. */
  initialStep?: 'choose' | 'copy'
  /** EDITING an icon with uploaded art: the pictures, recovered from the drawing
   *  (the metadata carries none — see `extractInlinedIconAssets`). Without them
   *  the maker opens with the art missing and hands back a spec that still
   *  claims it. */
  initialAssets?: { bg?: string; fg?: string }
}

export function AddPdappIconDialog({
  onChoose,
  onClose,
  excludeFolderId,
  tenantId,
  note,
  targetFile,
  initialSpec = null,
  initialLink = false,
  initialAssets,
  replacing = false,
  placement,
  initialStep = 'choose',
}: AddPdappIconDialogProps) {
  const { settings } = useTheme()
  // Hardcoded English was tolerable while this was editor-only; it renders in
  // the CHAT now, beside surfaces that translate.
  const { t } = useTranslation('collab')
  const toast = useToast()
  const themeClass = getThemeClass(settings.theme.workspace)
  // NOT `useIncomingSharedProjects()`. Those rows are `.shared` POINTERS: the id
  // is the pointer row in the viewer's own tenant and the name keeps the suffix,
  // so the export refuses every one of them (`is_folder = false`, and
  // `Foo.pdapp.shared` is not a PDApp name). Listing them advertised cross-tenant
  // targets that always failed. Reaching them needs the pointer dereferenced to
  // its source id + tenant, which is #676's picker — not a filter here.
  const [step, setStep] = useState<'choose' | 'copy'>(initialStep)
  // Editing a DESIGNED icon opens the maker directly — the chooser would ask a
  // question that was already answered when it was made.
  const [designOpen, setDesignOpen] = useState(!!initialSpec)
  // The link is an OPTION ON the icon, and it is OFF: an author who wants a
  // picture gets a picture. Ticking it is the moment they ask for a button.
  // Editing an icon that already opens its app starts with it kept.
  const [alsoOpen, setAlsoOpen] = useState(initialLink)
  // The copy step's explanations (what the option does, that the icon is a
  // copy) live behind an ⓘ instead of taking two bands of the dialog. A click
  // popover, not a hover tooltip, so it is reachable on touch.
  const [aboutOpen, setAboutOpen] = useState(false)
  const aboutRef = useRef<HTMLDivElement>(null)
  // The ⓘ belongs to the copy step: leaving it (Back, by mouse OR keyboard) must
  // not leave a closed-looking popover holding the next Escape and a listener —
  // so both ways out close it in the same update.
  const backToChooser = () => { setAboutOpen(false); setStep('choose') }
  useEffect(() => {
    if (!aboutOpen) return
    const onDown = (e: MouseEvent) => {
      if (!aboutRef.current?.contains(e.target as Node)) setAboutOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [aboutOpen])

  // The export is a round trip (it re-encodes art through sharp). Closing the
  // dialog must abandon an in-flight pick: without this the snippet arrived after
  // the author had cancelled, into whatever document they had switched to.
  const cancelledRef = useRef(false)
  useEffect(() => {
    // Set on SETUP as well as cleanup. `<StrictMode>` mounts, tears down and
    // re-mounts: with only a cleanup, that first teardown latches `true` forever
    // and every pick silently returns after its round trip — no snippet, no
    // error, the row stuck spinning. Dev-only, which is where this is used.
    cancelledRef.current = false
    return () => { cancelledRef.current = true }
  }, [])
  // Produce mode closes the icon maker itself the moment it hands the design
  // over, but assembling the metadata is async (the glyph). This says "that close
  // was the handover, not a cancel", so the maker stays on screen for the gap
  // instead of flashing back to the chooser.
  const producedRef = useRef(false)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // An open ⓘ closes first; the dialog stays.
      if (aboutOpen) { setAboutOpen(false); return }
      // The icon maker owns Escape while it is open (it has its own handler and
      // its own unsaved design to keep).
      if (designOpen) return
      // `copy` is the ROOT when the caller opened there: navigating to the
      // chooser would show a question the caller already asked, and Escape
      // would need two presses to dismiss one modal.
      if (step === 'copy' && initialStep !== 'copy') { backToChooser(); return }
      onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose, step, designOpen, initialStep, aboutOpen])

  const pick = async (meta: PortablePdappMetadata, name: string): Promise<boolean> => {
    const res = await onChoose({
      meta,
      markup: buildPdappIconMarkup(meta, { link: alsoOpen, size: PDAPP_ICON_DEFAULT_PX }),
      info: { kind: 'copied', name, linked: alsoOpen, artMissing: artDidNotTravel(meta) },
    })
    if (cancelledRef.current) return true
    if (!res.ok) {
      // Thrown, not returned: the picker tells the two failures apart by where
      // they came from, and this one is not the app's fault.
      throw new Error(res.error ?? t('addIcon.insertFailed'))
    }
    onClose()
    return true
  }

  const produce = async (spec: MiniappIconSpec, assets: { bg?: string; fg?: string }) => {
    try {
      const meta = await metadataFromDesignedIcon(spec, assets)
      if (cancelledRef.current) return
      const res = await onChoose({
        meta,
        markup: buildPdappIconMarkup(meta, { size: PDAPP_ICON_DEFAULT_PX }),
        info: { kind: 'designed', linked: false },
      })
      if (cancelledRef.current) return
      if (!res.ok) {
        // Keep the maker OPEN with the design intact — the insert can be refused
        // for reasons that have nothing to do with the icon (the document went
        // read-only while they were designing), and closing would throw away work
        // they just did over something they can retry.
        producedRef.current = false
        toast.error(res.error ?? t('addIcon.insertFailed'))
        return
      }
      onClose()
    } catch {
      if (cancelledRef.current) return
      // Keep the maker OPEN and the design intact — closing it would throw away
      // work the author just did, over a failure they can simply retry. A toast,
      // because the error belongs to the surface they are looking at and this
      // dialog's own error line is behind it.
      producedRef.current = false
      toast.error(t('addIcon.prepareFailed'))
    }
  }

  // The icon maker REPLACES this dialog rather than stacking on it: two modals
  // deep, the backdrops compound and the one underneath is unreachable anyway.
  if (designOpen) {
    return (
      <MiniappIconEditorDialog
        // No app behind a designed icon — a placeholder name the maker only uses
        // to seed its first draft, and an id it never calls (produce mode does no
        // upload and no save). EDITING one seeds the draft from what it already
        // looks like, through the same field a stored icon arrives in.
        file={{
          id: '',
          name: 'Icon.pdapp',
          metadataJson: initialSpec ? JSON.stringify({ [MINIAPP_ICON_METADATA_KEY]: initialSpec }) : null,
        }}
        tenantId={tenantId ?? undefined}
        initialAssets={initialAssets}
        placement={placement}
        onProduce={(spec, assets) => {
          // Once. The maker stays on screen while the metadata is assembled (the
          // glyph is a lazy import), so a second click in that gap would insert
          // the same icon twice.
          if (producedRef.current) return
          producedRef.current = true
          void produce(spec, assets)
        }}
        onClose={() => { if (producedRef.current) return; setDesignOpen(false) }}
      />
    )
  }

  const chooser = (
    <div className="px-2 py-2 flex flex-col gap-1.5">
      <button
        type="button"
        data-testid="add-icon-design"
        onClick={() => setDesignOpen(true)}
        className="w-full flex items-center gap-3 px-3 py-3 rounded text-left hover:themed-bg-tertiary"
      >
        <Palette size={18} className="shrink-0 themed-text-secondary" />
        <span className="min-w-0 flex-1">
          <span className="block text-sm themed-text">{t('addIcon.designOne')}</span>
          <span className="block text-xs themed-text-muted">{t('addIcon.designHint')}</span>
        </span>
        <ChevronRight size={14} className="shrink-0 themed-text-muted" />
      </button>
      <button
        type="button"
        data-testid="add-icon-copy"
        onClick={() => setStep('copy')}
        className="w-full flex items-center gap-3 px-3 py-3 rounded text-left hover:themed-bg-tertiary"
      >
        <Blocks size={18} className="shrink-0 themed-text-secondary" />
        <span className="min-w-0 flex-1">
          <span className="block text-sm themed-text">{t('addIcon.copyOne')}</span>
          <span className="block text-xs themed-text-muted">{t('addIcon.copyHint')}</span>
        </span>
        <ChevronRight size={14} className="shrink-0 themed-text-muted" />
      </button>
    </div>
  )

  const copyStep = (
    <PdappAppPicker
      excludeFolderId={excludeFolderId}
      onPick={pick}
      maxHeightClass="flex-1"
    />
  )

  // ONE row under the list for both options: they qualify the pick rather than
  // being part of the search, and a row per option was most of the dialog's
  // chrome. The placement sits on the right, so its menu aligns right.
  const copyOptions = (
    <div className="flex items-center justify-between gap-3 px-4 py-2 border-t themed-border">
      <label className="flex items-center gap-2 min-w-0 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={alsoOpen}
          onChange={(e) => setAlsoOpen(e.target.checked)}
          data-testid="add-icon-also-open"
          // The one themed checkbox in the app: a native box ignores the theme
          // and is barely visible on a dark dialog.
          className="a2ui-checkbox shrink-0"
        />
        <span className="text-xs themed-text-secondary truncate">{t('addIcon.alsoOpen')}</span>
      </label>
      {placement && placement.options.length > 0 && (
        <PlacementPicker
          label={placement.label}
          options={placement.options}
          value={placement.value}
          onChange={placement.onChange}
          menuAlign="right"
        />
      )}
    </div>
  )

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`${themeClass} relative themed-bg-secondary rounded-lg shadow-2xl w-[460px] max-w-[92vw] border themed-border flex flex-col max-h-[70vh]`}>
        <div className="relative flex items-center justify-between px-4 py-3 border-b themed-border">
          <div className="flex items-center gap-2 min-w-0">
            {step === 'copy' && initialStep !== 'copy' ? (
              <button
                type="button"
                onClick={backToChooser}
                className="themed-text-muted hover:themed-text"
                aria-label={t('addIcon.back')}
              >
                <ArrowLeft size={16} />
              </button>
            ) : (
              <Blocks size={16} className="themed-text-secondary" />
            )}
            <h3 className="text-sm font-medium themed-text truncate">
              {step === 'copy'
                ? t('addIcon.titleCopy')
                : replacing ? t('addIcon.titleReplace') : t('addIcon.titleAdd')}
            </h3>
          </div>
          <div className="flex items-center gap-2.5 shrink-0">
            {step === 'copy' && (
              <div ref={aboutRef}>
                <button
                  type="button"
                  onClick={() => setAboutOpen((v) => !v)}
                  aria-expanded={aboutOpen}
                  aria-label={t('addIcon.about')}
                  title={t('addIcon.about')}
                  data-testid="add-icon-about"
                  className={aboutOpen ? 'themed-text' : 'themed-text-muted hover:themed-text'}
                >
                  <Info size={15} />
                </button>
                {aboutOpen && (
                  <div
                    role="note"
                    data-testid="add-icon-about-panel"
                    className="absolute right-3 top-full mt-1 z-20 w-[300px] max-w-[calc(100%-1.5rem)] rounded-lg border themed-border themed-bg-secondary shadow-xl px-3 py-2.5 space-y-2 text-xs themed-text-secondary"
                  >
                    <p>{t('addIcon.alsoOpenHint')}</p>
                    <p className="themed-text-muted">{t('addIcon.footer')}</p>
                  </div>
                )}
              </div>
            )}
            <button onClick={onClose} className="themed-text-muted hover:themed-text" aria-label={t('addIcon.close')}>
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Why this opened, when it was the agent that opened it. */}
        {note && (
          <div className="px-4 py-2 border-b themed-border text-xs themed-text-secondary" data-testid="add-icon-note">
            {note}
          </div>
        )}
        {/* WHERE it will land. The agent can open this over any editor, and the
            dialog is portalled to the body, so it looks like it belongs to
            whatever is on screen — naming the file is the only way to notice. */}
        {targetFile && (
          <div className="px-4 py-1.5 border-b themed-border text-[11px] themed-text-muted truncate" data-testid="add-icon-target">
            {replacing ? t('addIcon.replacesIn') : t('addIcon.goesInto')}
            <span className="themed-text-secondary">{targetFile}</span>
          </div>
        )}

        {step === 'copy' ? copyStep : chooser}

        {step === 'copy' ? copyOptions : (
          <div className="px-4 py-2.5 border-t themed-border text-[11px] themed-text-muted">
            {t('addIcon.footer')}
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}
